import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useAltHotkeys } from '../hooks/useAltHotkeys';
import { useModalDrag } from '../hooks/useModalDrag';
import ModalHeader from './ui/ModalHeader';
import {
  ArrowPathIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { GoogleMap, Polyline, Marker, Circle, Polygon, useJsApiLoader } from '@react-google-maps/api';
import { getGoogleMapsLoaderOptions } from '../config/googleMaps';
import api from '../services/api';

interface LocationPoint {
  latitude: number;
  longitude: number;
  time: string;
  heading?: number;
  speed?: number;
}

interface GeofenceData {
  name: string | null;
  coordinates: string | null;
  geofenceType: string | null;
  geofenceRadius: number | null;
}

interface TravelHistoryResponse {
  success: boolean;
  proNumber: string;
  legNum: number;
  unitNumber: string;
  samsaraVehicleId: string;
  shipmentDate: string;
  startTime: string;
  endTime: string;
  locationCount: number;
  locations: LocationPoint[];
  origin: GeofenceData;
  destination: GeofenceData;
}

interface TravelHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  proNumber: string;
  legNum: number;
}

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const defaultCenter = { lat: 29.7604, lng: -95.3698 };

const formatTimestamp = (isoString: string): string => {
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'America/Chicago',
  });
};

const getDisplayPoints = (locations: LocationPoint[], maxPoints: number): LocationPoint[] => {
  if (locations.length <= maxPoints) return locations;

  const interval = Math.ceil(locations.length / maxPoints);
  const result: LocationPoint[] = [];

  for (let i = 0; i < locations.length; i += interval) {
    result.push(locations[i]);
  }

  if (result[result.length - 1] !== locations[locations.length - 1]) {
    result.push(locations[locations.length - 1]);
  }

  return result;
};

const parseCoordinates = (coordStr: string | null): { lat: number; lng: number }[] | null => {
  if (!coordStr) return null;
  try {
    // Clean the string (remove carriage returns that might be in the data)
    const cleaned = coordStr.replace(/\r/g, '').trim();

    if (cleaned.includes(';')) {
      return cleaned.split(';').map(pair => {
        const [lat, lng] = pair.split(',').map(Number);
        return { lat, lng };
      }).filter(p => !isNaN(p.lat) && !isNaN(p.lng));
    } else {
      const [lat, lng] = cleaned.split(',').map(Number);
      if (isNaN(lat) || isNaN(lng)) return null;
      return [{ lat, lng }];
    }
  } catch (e) {
    console.error('Error parsing coordinates:', coordStr, e);
    return null;
  }
};

const TravelHistoryModal: React.FC<TravelHistoryModalProps> = ({
  isOpen,
  onClose,
  proNumber,
  legNum,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TravelHistoryResponse | null>(null);
  const [hoveredPoint, setHoveredPoint] = useState<{ point: LocationPoint; x: number; y: number } | null>(null);
  const [dotDensity, setDotDensity] = useState(100);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);

  const { isLoaded } = useJsApiLoader(getGoogleMapsLoaderOptions());

  // Fetch data when modal opens
  useEffect(() => {
    if (isOpen && proNumber && legNum) {
      fetchTravelHistory();
    }
    if (!isOpen) {
      setData(null);
      setError(null);
      setHoveredPoint(null);
    }
  }, [isOpen, proNumber, legNum]);

  // Cleanup markers on unmount
  useEffect(() => {
    return () => {
      markersRef.current.forEach(marker => {
        marker.map = null;
      });
      markersRef.current = [];
    };
  }, []);

  // Create start/end markers when data changes
  useEffect(() => {
    if (!mapRef.current || !data?.locations?.length || !window.google?.maps?.marker?.AdvancedMarkerElement) {
      return;
    }

    markersRef.current.forEach(marker => {
      marker.map = null;
    });
    markersRef.current = [];

    const locations = data.locations;
    const startLoc = locations[0];
    const endLoc = locations[locations.length - 1];

    const startEl = document.createElement('div');
    startEl.innerHTML = `
      <div style="display: flex; flex-direction: column; align-items: center;">
        <div style="background: #16A34A; color: white; padding: 2px 6px; border-radius: 4px; font-size: 11px; font-weight: bold; margin-bottom: 4px; white-space: nowrap;">
          START
        </div>
        <div style="width: 14px; height: 14px; background: #16A34A; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.4);"></div>
      </div>
    `;
    const startMarker = new google.maps.marker.AdvancedMarkerElement({
      position: { lat: startLoc.latitude, lng: startLoc.longitude },
      map: mapRef.current,
      content: startEl,
      title: `Start: ${formatTimestamp(startLoc.time)} CST`,
      zIndex: 1000,
    });
    markersRef.current.push(startMarker);

    const endEl = document.createElement('div');
    endEl.innerHTML = `
      <div style="display: flex; flex-direction: column; align-items: center;">
        <div style="background: #DC2626; color: white; padding: 2px 6px; border-radius: 4px; font-size: 11px; font-weight: bold; margin-bottom: 4px; white-space: nowrap;">
          END
        </div>
        <div style="width: 14px; height: 14px; background: #DC2626; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.4);"></div>
      </div>
    `;
    const endMarker = new google.maps.marker.AdvancedMarkerElement({
      position: { lat: endLoc.latitude, lng: endLoc.longitude },
      map: mapRef.current,
      content: endEl,
      title: `End: ${formatTimestamp(endLoc.time)} CST`,
      zIndex: 1000,
    });
    markersRef.current.push(endMarker);
  }, [data]);

  const fetchTravelHistory = async () => {
    setLoading(true);
    setError(null);
    setData(null);

    try {
      const response = await api.get(`/driver-dispatch/travel-history/${proNumber}`, {
        params: { legnum: legNum }
      });
      setData(response.data);

      if (response.data.locations?.length > 0) {
        const locs = response.data.locations;
        const lats = locs.map((l: LocationPoint) => l.latitude);
        const lngs = locs.map((l: LocationPoint) => l.longitude);
        setMapCenter({
          lat: (Math.min(...lats) + Math.max(...lats)) / 2,
          lng: (Math.min(...lngs) + Math.max(...lngs)) / 2,
        });

        if (mapRef.current) {
          const bounds = new google.maps.LatLngBounds();
          locs.forEach((loc: LocationPoint) => {
            bounds.extend({ lat: loc.latitude, lng: loc.longitude });
          });
          mapRef.current.fitBounds(bounds, 50);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch travel history');
    } finally {
      setLoading(false);
    }
  };

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;

    // Fit bounds if data already loaded
    if (data?.locations?.length) {
      const bounds = new google.maps.LatLngBounds();
      data.locations.forEach((loc: LocationPoint) => {
        bounds.extend({ lat: loc.latitude, lng: loc.longitude });
      });
      map.fitBounds(bounds, 50);
    }
  }, [data]);

  const handleDotMouseOver = (point: LocationPoint, e: google.maps.MapMouseEvent) => {
    if (e.domEvent) {
      const mouseEvent = e.domEvent as MouseEvent;
      setHoveredPoint({
        point,
        x: mouseEvent.clientX,
        y: mouseEvent.clientY,
      });
    }
  };

  const handleDotMouseOut = () => {
    setHoveredPoint(null);
  };

  // Read-only GPS history: Alt+C closes; the layer keeps Alt+S off the grid.
  useAltHotkeys({ C: onClose }, undefined, { layer: isOpen });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(isOpen);

  if (!isOpen) return null;

  const polylinePath = data?.locations?.map(loc => ({
    lat: loc.latitude,
    lng: loc.longitude,
  })) || [];

  const displayPoints = data?.locations ? getDisplayPoints(data.locations, dotDensity) : [];

  // Portaled to document.body so the sidebar / drawer / any parent
  // stacking context can't clip the modal. Same pattern as
  // ProDetailSlideOver.
  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-scrim bg-opacity-50" onClick={onClose} />

      {/* Modal — centered, 75% of viewport on lg+, with sensible min/max bounds */}
      <div
        ref={panelRef}
        style={panelStyle}
        className="relative z-[101] bg-white dark:bg-gray-900 rounded-xl shadow-2xl flex flex-col overflow-hidden w-[75vw] h-[75vh] max-w-[1400px] max-h-[900px] min-w-[640px] min-h-[480px]"
      >
        <ModalHeader
          title={
            <span className="flex items-center gap-3">
              <MapPinIcon className="h-6 w-6 text-blue-300" />
              <span>
                <span className="block">Driver Travel History</span>
                <span className="block text-sm font-normal text-slate-300">
                  PRO: {proNumber} - Leg {legNum}
                  {data && ` • ${data.unitNumber} • ${data.shipmentDate}`}
                </span>
              </span>
            </span>
          }
          onClose={onClose}
          dragHandleProps={dragHandleProps}
        />

        {/* Map Container */}
        <div className="flex-1 relative">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
              <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600 dark:text-gray-300">Loading travel history...</span>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
              <div className="text-center p-4">
                <div className="text-red-600 font-medium mb-2">Error</div>
                <div className="text-gray-600 dark:text-gray-300">{error}</div>
              </div>
            </div>
          )}

          {isLoaded && !loading && !error && (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={mapCenter}
              zoom={10}
              onLoad={onMapLoad}
              options={{
                mapTypeControl: true,
                streetViewControl: false,
                fullscreenControl: true,
                mapId: 'travel-history-modal-map',
              }}
            >
              {polylinePath.length > 0 && (
                <Polyline
                  path={polylinePath}
                  options={{
                    strokeColor: '#2563EB',
                    strokeOpacity: 0.8,
                    strokeWeight: 3,
                  }}
                />
              )}

              {displayPoints.map((point, index) => (
                <Circle
                  key={index}
                  center={{ lat: point.latitude, lng: point.longitude }}
                  radius={30}
                  options={{
                    fillColor: '#3B82F6',
                    fillOpacity: 0.8,
                    strokeColor: '#1D4ED8',
                    strokeWeight: 1,
                    clickable: true,
                    zIndex: 100,
                  }}
                  onMouseOver={(e) => handleDotMouseOver(point, e)}
                  onMouseOut={handleDotMouseOut}
                />
              ))}

              {/* Origin Geofence */}
              {(() => {
                const originCoords = parseCoordinates(data?.origin?.coordinates || null);
                if (!originCoords) return null;

                const isCircular = data?.origin?.geofenceType?.toUpperCase() === 'CIRCULAR';
                const radiusMeters = (data?.origin?.geofenceRadius || 0.5) * 1609.34;

                return (
                  <>
                    {isCircular ? (
                      <Circle
                        center={originCoords[0]}
                        radius={radiusMeters}
                        options={{
                          fillColor: '#10B981',
                          fillOpacity: 0.2,
                          strokeColor: '#10B981',
                          strokeWeight: 2,
                          zIndex: 50,
                        }}
                      />
                    ) : (
                      <Polygon
                        paths={originCoords}
                        options={{
                          fillColor: '#10B981',
                          fillOpacity: 0.2,
                          strokeColor: '#10B981',
                          strokeWeight: 2,
                          zIndex: 50,
                        }}
                      />
                    )}
                    <Marker
                      position={originCoords[0]}
                      label={{
                        text: 'O',
                        color: '#FFFFFF',
                        fontSize: '14px',
                        fontWeight: 'bold',
                      }}
                      icon={{
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 12,
                        fillColor: '#10B981',
                        fillOpacity: 1,
                        strokeColor: '#FFFFFF',
                        strokeWeight: 2,
                      }}
                      zIndex={200}
                    />
                  </>
                );
              })()}

              {/* Destination Geofence */}
              {(() => {
                const destCoords = parseCoordinates(data?.destination?.coordinates || null);
                if (!destCoords) return null;

                const isCircular = data?.destination?.geofenceType?.toUpperCase() === 'CIRCULAR';
                const radiusMeters = (data?.destination?.geofenceRadius || 0.5) * 1609.34;

                return (
                  <>
                    {isCircular ? (
                      <Circle
                        center={destCoords[0]}
                        radius={radiusMeters}
                        options={{
                          fillColor: '#3B82F6',
                          fillOpacity: 0.2,
                          strokeColor: '#3B82F6',
                          strokeWeight: 2,
                          zIndex: 50,
                        }}
                      />
                    ) : (
                      <Polygon
                        paths={destCoords}
                        options={{
                          fillColor: '#3B82F6',
                          fillOpacity: 0.2,
                          strokeColor: '#3B82F6',
                          strokeWeight: 2,
                          zIndex: 50,
                        }}
                      />
                    )}
                    <Marker
                      position={destCoords[0]}
                      label={{
                        text: 'D',
                        color: '#FFFFFF',
                        fontSize: '14px',
                        fontWeight: 'bold',
                      }}
                      icon={{
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 12,
                        fillColor: '#3B82F6',
                        fillOpacity: 1,
                        strokeColor: '#FFFFFF',
                        strokeWeight: 2,
                      }}
                      zIndex={200}
                    />
                  </>
                );
              })()}
            </GoogleMap>
          )}

          {/* Hover Tooltip */}
          {hoveredPoint && (
            <div
              className="fixed z-[9999] bg-gray-900 text-white text-sm rounded-lg px-3 py-2 shadow-lg pointer-events-none"
              style={{
                left: hoveredPoint.x + 10,
                top: hoveredPoint.y - 40,
              }}
            >
              <div className="font-medium">{formatTimestamp(hoveredPoint.point.time)} CST</div>
              <div className="text-gray-300">
                Speed: {hoveredPoint.point.speed !== undefined ? `${Math.round(hoveredPoint.point.speed)} mph` : 'N/A'}
              </div>
            </div>
          )}
        </div>

        {/* Footer with Slider */}
        {data && (
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600 dark:text-gray-300">
                <span className="font-medium">{data.locationCount.toLocaleString()}</span> GPS points
                {' • '}
                <span className="font-medium">{displayPoints.length}</span> dots shown
              </div>
              <div className="flex items-center gap-3">
                <label className="text-sm text-gray-600 dark:text-gray-300">Dot density:</label>
                <input
                  type="range"
                  min="25"
                  max="500"
                  step="25"
                  value={dotDensity}
                  onChange={(e) => setDotDensity(parseInt(e.target.value, 10))}
                  className="w-40 h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default TravelHistoryModal;
